import { useEffect, useRef, useState } from 'react';

/**
 * Задача L1: throttled-версия значения — коммитит новое значение не чаще, чем
 * раз в `delayMs`, но гарантированно "дотягивает" до самого свежего значения
 * (trailing edge), даже если обновления перестали приходить сразу после коммита.
 *
 * Используется в панели прогресса обработки: сырые elapsed_sec/eta_sec приходят
 * с сервера каждые ~4с, но текст "Прошло / Осталось" не должен дёргаться чаще
 * раза в 10с — иначе минуты мелькают на каждый тик поллинга.
 */
export function useThrottledValue<T>(value: T, delayMs: number): T {
  const [throttled, setThrottled] = useState(value);
  const lastCommitRef = useRef(0);

  useEffect(() => {
    const now = Date.now();
    const sinceLastCommit = now - lastCommitRef.current;

    if (sinceLastCommit >= delayMs) {
      lastCommitRef.current = now;
      setThrottled(value);
      return;
    }

    const timer = setTimeout(() => {
      lastCommitRef.current = Date.now();
      setThrottled(value);
    }, delayMs - sinceLastCommit);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return throttled;
}
